import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { MarkdownContent } from './MarkdownContent';
import { TrainingNavItem } from '../../services/trainingService';

interface HelpMobilePaneProps {
  title: string;
  content: string;
  loading: boolean;
  error: string | null;
  docs: TrainingNavItem[];
  onNavigateToDoc: (docId: string) => void;
  onBack: () => void;
}

/**
 * Mobile reading pane. On a phone the contents list fills the drawer, so
 * opening a document covers it entirely rather than sitting beside it.
 */
export const HelpMobilePane: React.FC<HelpMobilePaneProps> = ({
  title,
  content,
  loading,
  error,
  docs,
  onNavigateToDoc,
  onBack,
}) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'background.default',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideInRight 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes slideInRight': {
        from: {
          transform: 'translateX(100%)',
          opacity: 0,
        },
        to: {
          transform: 'translateX(0)',
          opacity: 1,
        },
      },
      '@media print': {
        display: 'none',
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      {/* A back arrow, not a close X. On a phone the manual opens straight
          into a document, so this is the only way to reach the contents list
          and an X reads as "close the manual". */}
      <IconButton
        onClick={onBack}
        size="small"
        aria-label="Back to contents"
        sx={{ mr: 1, color: 'inherit' }}
      >
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
        {title}
      </Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        '@media print': {
          overflow: 'visible',
          flex: 'none',
        },
      }}
    >
      <MarkdownContent
        content={content}
        loading={loading}
        error={error}
        onNavigateToDoc={onNavigateToDoc}
        docs={docs}
      />
    </Box>
  </Box>
);
